import { useState } from "react";
import type { FbAccount } from "../lib/api.ts";
import { api } from "../lib/api.ts";
import { accountStatus, spendCapFromMinor } from "../lib/format.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Badge, Button, InfoBanner, Page, Table } from "../ui/index.ts";
import { badgeVariant } from "./Overview.tsx";

export function Accounts() {
	const [accounts, reload] = useLoad(() => api.fbAccounts());
	const [busy, setBusy] = useState(false);
	// A failed sync and a successful one are not the same news. Both used to land
	// in one `note` rendered as an info banner, so "Meta API error 190: token
	// expired" arrived looking exactly like "Pulled 4 accounts".
	const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);

	async function sync() {
		setBusy(true);
		setNote(null);
		try {
			const { synced } = await api.syncFbAccounts();
			setNote({
				text: `Pulled ${synced} account${synced === 1 ? "" : "s"} from Meta.`,
				failed: false,
			});
			reload();
		} catch (e) {
			setNote({ text: e instanceof Error ? e.message : String(e), failed: true });
		} finally {
			setBusy(false);
		}
	}

	const list = accounts.state === "ready" ? accounts.data.accounts : [];

	return (
		<Page
			title="Ad accounts"
			description="Read from this console's own database, not from Meta on every load. Pull a refresh when accounts change in Business Manager."
			actions={
				<Button onClick={() => void sync()} loading={busy}>
					Sync from Meta
				</Button>
			}
		>
			{note && <InfoBanner variant={note.failed ? "danger" : "info"} title={note.text} />}
			{accounts.state === "ready" && accounts.data.truncated && (
				<InfoBanner variant="warning" title="This list is not complete.">
					More rows exist than one read returns, so what you see here is the first 1000. Nothing is
					missing from the database — the list on this screen is short, and it will stay short until
					paging is added.
				</InfoBanner>
			)}
			{accounts.state === "failed" && (
				<InfoBanner variant="danger" title="Accounts could not be read.">
					{accounts.why}
				</InfoBanner>
			)}

			<Table<FbAccount>
				columns={[
					{
						id: "name",
						header: "Account",
						accessor: (a) => (
							<>
								<div className="font-medium text-content">{a.name}</div>
								<div className="font-mono text-xs text-muted">{a.id}</div>
							</>
						),
					},
					{
						id: "meta",
						header: "Meta id",
						accessor: (a) => <span className="font-mono text-xs">{a.metaAccountId}</span>,
					},
					{ id: "currency", header: "Currency", accessor: (a) => a.currency ?? "—" },
					{
						id: "cap",
						header: "Spending limit",
						accessor: (a) => {
							const cap = spendCapFromMinor(a.spendCap, a.currency);
							return cap.capped ? (
								cap.text
							) : (
								<span className="font-medium text-warning">{cap.text}</span>
							);
						},
					},
					{
						id: "state",
						header: "State",
						accessor: (a) => {
							const st = accountStatus(a.accountStatus ?? -1);
							return <Badge variant={badgeVariant(st.tone)}>{st.label}</Badge>;
						},
					},
					{
						id: "synced",
						header: "Synced",
						accessor: (a) => (
							<span className="text-muted">
								{a.syncedAt ? new Date(a.syncedAt).toLocaleString() : "never"}
							</span>
						),
					},
				]}
				data={list}
				keyExtractor={(a) => a.id}
				loading={accounts.state === "loading"}
				emptyMessage="Nothing synced yet. Once the Meta system-user token is configured, pull a refresh and the accounts it can reach appear here."
			/>
		</Page>
	);
}
